/**
 * Servicio para la Matriz Legal SST.
 * Maneja la comunicación con la API para:
 * - Importación de archivos Excel
 * - Gestión de normas
 * - Cumplimiento por empresa
 * - Dashboard y estadísticas
 * - Exportación
 */

import api from "./api";

// Sugerencias predefinidas para el campo "Responsable" — compartidas entre
// el modal de evaluación individual, los diálogos bulk y el mapeo de
// responsables por clasificación en el Perfil de la Empresa.
export const SUGERENCIAS_RESPONSABLES = [
  "Responsable del SG-SST",
  "Coordinador de SST",
  "Jefe de Talento Humano",
  "Gerente General",
  "Jefe de Operaciones",
  "Médico Ocupacional",
  "COPASST",
  "Vigía de SST",
  "Jefe de Mantenimiento",
  "Coordinador de Calidad",
  "ARL",
  "Asesor Externo SST",
  "Brigada de Emergencias",
  "Jefe de Producción",
  "Jefe de Almacén",
  "Supervisor de Área",
];

// ==================== TIPOS ====================

export type AmbitoAplicacion = "nacional" | "departamental" | "municipal" | "internacional";
export type EstadoNorma = "vigente" | "derogada" | "modificada";
export type EstadoCumplimiento = "pendiente" | "cumple" | "no_cumple" | "no_aplica" | "en_proceso";
export type EstadoImportacion = "en_proceso" | "completada" | "fallida" | "parcial";

export interface SectorEconomico {
  id: number;
  codigo: string | null;
  nombre: string;
  descripcion: string | null;
  es_todos_los_sectores: boolean;
  ciiu_prefijos: string | null;
  activo: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface SectorEconomicoSimple {
  id: number;
  nombre: string;
  es_todos_los_sectores: boolean;
}

export interface EmpresaCaracteristicas {
  tiene_trabajadores_independientes: boolean;
  tiene_teletrabajo: boolean;
  tiene_trabajo_alturas: boolean;
  tiene_trabajo_espacios_confinados: boolean;
  tiene_trabajo_caliente: boolean;
  tiene_sustancias_quimicas: boolean;
  tiene_radiaciones: boolean;
  tiene_trabajo_nocturno: boolean;
  tiene_menores_edad: boolean;
  tiene_mujeres_embarazadas: boolean;
  tiene_conductores: boolean;
  tiene_manipulacion_alimentos: boolean;
  tiene_maquinaria_pesada: boolean;
  tiene_riesgo_electrico: boolean;
  tiene_riesgo_biologico: boolean;
  tiene_trabajo_excavaciones: boolean;
  tiene_trabajo_administrativo: boolean;
}

export interface Empresa extends EmpresaCaracteristicas {
  id: number;
  nombre: string;
  nit: string | null;
  razon_social: string | null;
  direccion: string | null;
  telefono: string | null;
  email: string | null;
  sector_economico_id: number | null;
  codigo_ciiu: string | null;
  sector_economico?: SectorEconomicoSimple;
  activo: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface EmpresaResumen {
  id: number;
  nombre: string;
  nit: string | null;
  sector_economico_nombre: string | null;
  activo: boolean;
  total_normas_aplicables: number;
  normas_cumple: number;
  normas_no_cumple: number;
  normas_pendientes: number;
  porcentaje_cumplimiento: number;
}

export interface MatrizLegalNorma {
  id: number;
  ambito_aplicacion: AmbitoAplicacion;
  sector_economico_id: number | null;
  sector_economico_texto: string | null;
  clasificacion_norma: string;
  tema_general: string;
  subtema_riesgo_especifico: string | null;
  anio: number;
  tipo_norma: string;
  numero_norma: string;
  fecha_expedicion: string | null;
  expedida_por: string | null;
  descripcion_norma: string | null;
  articulo: string | null;
  estado: EstadoNorma;
  info_adicional: string | null;
  descripcion_articulo_exigencias: string | null;
  aplica_general: boolean;
  aplica_teletrabajo: boolean;
  aplica_trabajo_alturas: boolean;
  aplica_espacios_confinados: boolean;
  aplica_sustancias_quimicas: boolean;
  aplica_conductores: boolean;
  aplica_riesgo_electrico: boolean;
  aplica_riesgo_biologico: boolean;
  aplica_trabajo_nocturno: boolean;
  aplica_menores_edad: boolean;
  aplica_mujeres_embarazadas: boolean;
  aplica_trabajo_administrativo: boolean;
  /**
   * Avisos, no filtros: la norma aplica igual, pero cómo se cumple depende del
   * número de trabajadores o de la clase de riesgo (p. ej. COPASST vs Vigía).
   */
  requiere_revision_tamano: boolean;
  requiere_revision_riesgo: boolean;
  version: number;
  activo: boolean;
  created_at: string;
  updated_at: string | null;
  sector_economico_nombre?: string;
  identificador_completo?: string;
}

export interface MatrizLegalNormaConCumplimiento extends MatrizLegalNorma {
  cumplimiento_id: number | null;
  estado_cumplimiento: EstadoCumplimiento | null;
  aplica_empresa: boolean;
  evidencia_cumplimiento: string | null;
  observaciones: string | null;
  plan_accion: string | null;
  responsable: string | null;
  fecha_compromiso: string | null;
  fecha_ultima_evaluacion: string | null;
  /** Solo presente si requiere_revision_tamano/riesgo — ver backend para el detalle. */
  revision_hint: string | null;
}

export interface MatrizLegalCumplimiento {
  id: number;
  empresa_id: number;
  norma_id: number;
  estado: EstadoCumplimiento;
  evidencia_cumplimiento: string | null;
  observaciones: string | null;
  plan_accion: string | null;
  responsable: string | null;
  fecha_compromiso: string | null;
  seguimiento: string | null;
  aplica_empresa: boolean;
  justificacion_no_aplica: string | null;
  fecha_ultima_evaluacion: string | null;
  fecha_proxima_revision: string | null;
  evaluado_por: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface MatrizLegalCumplimientoHistorial {
  id: number;
  cumplimiento_id: number;
  estado_anterior: string | null;
  estado_nuevo: string;
  observaciones: string | null;
  created_at: string;
  creado_por: number;
  usuario_nombre: string | null;
}

export interface MatrizLegalImportacionPreview {
  total_filas: number;
  normas_nuevas_preview: number;
  normas_existentes_preview: number;
  errores_validacion: { fila: number; error: string }[];
  columnas_detectadas: string[];
  columnas_mapeadas: Record<string, string | null>; // Original -> Mapeado (o null si no mapea)
  muestra_datos: Record<string, unknown>[];
}

export interface MatrizLegalImportacionResult {
  id: number;
  nombre_archivo: string;
  fecha_importacion: string;
  estado: EstadoImportacion;
  total_filas: number;
  normas_nuevas: number;
  normas_actualizadas: number;
  normas_sin_cambios: number;
  errores: number;
  log_errores: string | null;
  creado_por: number;
  /** Sincronización automática de cumplimientos por empresa tras la importación */
  sincronizacion?: SincronizacionEmpresaResult[];
}

export interface SincronizacionEmpresaResult {
  empresa_id: number;
  empresa_nombre: string;
  nuevos_creados: number;
  reactivados: number;
  desactivados: number;
  error: string | null;
}

export interface RecalculoAplicabilidadResult {
  total: number;
  marcadas_especificas: number;
  marcadas_generales: number;
  marcadas_revision_tamano?: number;
  marcadas_revision_riesgo?: number;
}

export interface MatrizLegalEstadisticasPorEstado {
  cumple: number;
  no_cumple: number;
  pendiente: number;
  no_aplica: number;
  en_proceso: number;
}

export interface MatrizLegalEstadisticas {
  empresa_id: number;
  empresa_nombre: string;
  total_normas_aplicables: number;
  por_estado: MatrizLegalEstadisticasPorEstado;
  porcentaje_cumplimiento: number;
  normas_con_plan_accion: number;
  normas_vencidas: number;
  /** Cumplimientos heredados de un perfil anterior (aplica_empresa=false) */
  normas_no_aplicables?: number;
  /** Normas vigentes cuyo cumplimiento depende del tamaño / clase de riesgo */
  normas_revision_tamano?: number;
  normas_revision_riesgo?: number;
}

export interface MatrizLegalDashboard {
  estadisticas: MatrizLegalEstadisticas;
  ultimas_evaluaciones: { id: number; norma_id: number; estado: string; fecha: string }[];
  normas_criticas: MatrizLegalNormaConCumplimiento[];
  proximas_revisiones: unknown[];
  importaciones_recientes: MatrizLegalImportacionResult[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface SugerenciasIA {
  evidencia: string;
  observaciones: string;
  plan_accion: string;
}

export interface SugerenciasIAResponse {
  success: boolean;
  norma_id: number;
  sugerencias: SugerenciasIA;
}

export interface BulkUpdatePayload {
  cumplimiento_ids: number[];
  estado: EstadoCumplimiento;
  evidencia_cumplimiento?: string | null;
  plan_accion?: string | null;
  responsable?: string | null;
  fecha_compromiso?: string | null;
  observaciones?: string | null;
  aplica_empresa?: boolean | null;
  justificacion_no_aplica?: string | null;
}

export interface FiltrosBulkUpdatePayload {
  // Filtros activos en la UI
  estado_cumplimiento?: string;
  clasificacion?: string;
  tema_general?: string;
  q?: string;
  solo_aplicables?: boolean;
  // Datos a aplicar
  estado: EstadoCumplimiento;
  evidencia_cumplimiento?: string | null;
  plan_accion?: string | null;
  responsable?: string | null;
  fecha_compromiso?: string | null;
  observaciones?: string | null;
  aplica_empresa?: boolean | null;
  justificacion_no_aplica?: string | null;
}

/**
 * Genera sugerencias de IA distintas para CADA norma (no un texto único).
 * Si se envía `cumplimiento_ids` se usa esa selección; si no, los filtros.
 */
export interface SugerenciasIABulkPayload {
  cumplimiento_ids?: number[];
  estado_cumplimiento?: string;
  clasificacion?: string;
  tema_general?: string;
  q?: string;
  solo_aplicables?: boolean;
  solo_vacios?: boolean;
  sobrescribir_observaciones?: boolean;
}

export interface SugerenciasIAJobCreated {
  job_id: number;
  total: number;
}

export interface SugerenciasIAJobStatus {
  id: number;
  estado: EstadoImportacion;
  total: number;
  procesadas: number;
  exitosas: number;
  fallidas: number;
  log_errores: string | null;
  created_at: string;
  finished_at: string | null;
}

/** Responsable por defecto que le corresponde a la empresa según clasificación de norma. */
export interface MatrizLegalResponsableClasificacion {
  id: number;
  empresa_id: number;
  clasificacion_norma: string;
  responsable: string;
  created_at: string;
  updated_at: string | null;
}

export interface AutoasignarResponsablesResult {
  actualizados: number;
  sin_mapeo: number;
}

export interface ImportarCumplimientoResult {
  total_filas: number;
  actualizados: number;
  sin_cambios: number;
  errores: number;
  log_errores: string[];
}

// ==================== CIIU ====================

/**
 * Secciones CIIU Rev. 4 A.C.: rango de divisiones (2 primeros dígitos) y nombre.
 */
export const SECCIONES_CIIU: { letra: string; desde: string; hasta: string; nombre: string }[] = [
  { letra: "A", desde: "01", hasta: "03", nombre: "Agricultura, ganadería, caza, silvicultura y pesca" },
  { letra: "B", desde: "05", hasta: "09", nombre: "Explotación de minas y canteras" },
  { letra: "C", desde: "10", hasta: "33", nombre: "Industrias manufactureras" },
  { letra: "D", desde: "35", hasta: "35", nombre: "Suministro de electricidad, gas, vapor y aire acondicionado" },
  { letra: "E", desde: "36", hasta: "39", nombre: "Distribución de agua; saneamiento ambiental" },
  { letra: "F", desde: "41", hasta: "43", nombre: "Construcción" },
  { letra: "G", desde: "45", hasta: "47", nombre: "Comercio; reparación de vehículos automotores" },
  { letra: "H", desde: "49", hasta: "53", nombre: "Transporte y almacenamiento" },
  { letra: "I", desde: "55", hasta: "56", nombre: "Alojamiento y servicios de comida" },
  { letra: "J", desde: "58", hasta: "63", nombre: "Información y comunicaciones" },
  { letra: "K", desde: "64", hasta: "66", nombre: "Actividades financieras y de seguros" },
  { letra: "L", desde: "68", hasta: "68", nombre: "Actividades inmobiliarias" },
  { letra: "M", desde: "69", hasta: "75", nombre: "Actividades profesionales, científicas y técnicas" },
  { letra: "N", desde: "77", hasta: "82", nombre: "Actividades de servicios administrativos y de apoyo" },
  { letra: "O", desde: "84", hasta: "84", nombre: "Administración pública y defensa" },
  { letra: "P", desde: "85", hasta: "85", nombre: "Educación" },
  { letra: "Q", desde: "86", hasta: "88", nombre: "Atención de la salud humana y asistencia social" },
  { letra: "R", desde: "90", hasta: "93", nombre: "Actividades artísticas, de entretenimiento y recreación" },
  { letra: "S", desde: "94", hasta: "96", nombre: "Otras actividades de servicios" },
  { letra: "T", desde: "97", hasta: "98", nombre: "Actividades de los hogares como empleadores" },
  { letra: "U", desde: "99", hasta: "99", nombre: "Organizaciones y entidades extraterritoriales" },
];

/**
 * Devuelve la sección CIIU a la que pertenece un código (2-4 dígitos), o null.
 */
export function getSeccionCIIU(codigo: string | null | undefined) {
  const digitos = (codigo || "").replace(/\D/g, "");
  if (digitos.length < 2) return null;
  const division = digitos.slice(0, 2);
  return (
    SECCIONES_CIIU.find((s) => s.desde <= division && division <= s.hasta) || null
  );
}

// ==================== SERVICIO ====================

class MatrizLegalService {
  // ==================== SECTORES ECONÓMICOS ====================

  async listSectoresEconomicos(params?: { activo?: boolean; q?: string }) {
    const res = await api.get("/sectores-economicos/", { params });
    return res.data as SectorEconomico[];
  }

  async listSectoresActivos() {
    const res = await api.get("/sectores-economicos/activos");
    return res.data as SectorEconomicoSimple[];
  }

  async getSectorEconomico(id: number) {
    const res = await api.get(`/sectores-economicos/${id}`);
    return res.data as SectorEconomico;
  }

  async createSectorEconomico(data: Partial<SectorEconomico>) {
    const res = await api.post("/sectores-economicos/", data);
    return res.data as SectorEconomico;
  }

  async updateSectorEconomico(id: number, data: Partial<SectorEconomico>) {
    const res = await api.put(`/sectores-economicos/${id}`, data);
    return res.data as SectorEconomico;
  }

  async deleteSectorEconomico(id: number) {
    await api.delete(`/sectores-economicos/${id}`);
  }

  // ==================== EMPRESAS ====================

  async listEmpresas(params?: { activo?: boolean; q?: string }) {
    const res = await api.get("/empresas/", { params });
    return res.data as EmpresaResumen[];
  }

  async listEmpresasActivas() {
    const res = await api.get("/empresas/activas");
    return res.data as { id: number; nombre: string; nit: string | null }[];
  }

  async getEmpresa(id: number) {
    const res = await api.get(`/empresas/${id}`);
    return res.data as Empresa;
  }

  async updateEmpresa(id: number, data: Partial<Empresa>) {
    const res = await api.put(`/empresas/${id}`, data);
    return res.data as Empresa;
  }

  /** Empresa (tenant) del usuario autenticado. */
  async getMiEmpresa() {
    const res = await api.get("/empresas/mi-empresa");
    return res.data as Empresa;
  }

  /**
   * Actualiza el perfil de la propia empresa (sector, CIIU y características).
   * El backend ignora `activo`: suspender una empresa es cosa del superadmin.
   */
  async updateMiEmpresa(data: Partial<Empresa>) {
    const res = await api.put("/empresas/mi-empresa", data);
    return res.data as Empresa;
  }

  async sincronizarNormasEmpresa(empresaId: number) {
    const res = await api.post(`/empresas/${empresaId}/sincronizar-normas`);
    return res.data as { message: string };
  }

  async getCaracteristicasEmpresa(empresaId: number) {
    const res = await api.get(`/empresas/${empresaId}/caracteristicas`);
    return res.data as string[];
  }

  // ==================== IMPORTACIÓN ====================

  async previewImport(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.post("/matriz-legal/importar/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data as MatrizLegalImportacionPreview;
  }

  async importExcel(file: File, sobrescribir: boolean = false) {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.post(
      `/matriz-legal/importar?sobrescribir_existentes=${sobrescribir}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return res.data as MatrizLegalImportacionResult;
  }

  async listImportaciones(params?: { page?: number; size?: number }) {
    const res = await api.get("/matriz-legal/importaciones", { params });
    return res.data as PaginatedResponse<MatrizLegalImportacionResult>;
  }

  // ==================== NORMAS ====================

  async listNormas(params?: {
    page?: number;
    size?: number;
    q?: string;
    sector_economico_id?: number;
    clasificacion?: string;
    tema_general?: string;
    anio?: number;
    estado?: string;
    activo?: boolean;
  }) {
    const res = await api.get("/matriz-legal/normas", { params });
    return res.data as PaginatedResponse<MatrizLegalNorma>;
  }

  async getNorma(normaId: number) {
    const res = await api.get(`/matriz-legal/normas/${normaId}`);
    return res.data as MatrizLegalNorma;
  }

  async updateNorma(normaId: number, data: Partial<MatrizLegalNorma>) {
    const res = await api.put(`/matriz-legal/normas/${normaId}`, data);
    return res.data as MatrizLegalNorma;
  }

  async recalcularAplicabilidad() {
    const res = await api.post("/matriz-legal/normas/recalcular-aplicabilidad");
    return res.data as RecalculoAplicabilidadResult;
  }

  async getCatalogosClasificaciones() {
    const res = await api.get("/matriz-legal/normas/catalogos/clasificaciones");
    return res.data as string[];
  }

  async getCatalogosTemas(clasificacion?: string) {
    const res = await api.get("/matriz-legal/normas/catalogos/temas", {
      params: { clasificacion },
    });
    return res.data as string[];
  }

  async getCatalogosAnios() {
    const res = await api.get("/matriz-legal/normas/catalogos/anios");
    return res.data as number[];
  }

  // ==================== CUMPLIMIENTO POR EMPRESA ====================

  async getNormasEmpresa(
    empresaId: number,
    params?: {
      page?: number;
      size?: number;
      estado_cumplimiento?: string;
      clasificacion?: string;
      tema_general?: string;
      q?: string;
      solo_aplicables?: boolean;
    }
  ) {
    const res = await api.get(`/matriz-legal/empresas/${empresaId}/normas`, {
      params,
    });
    return res.data as PaginatedResponse<MatrizLegalNormaConCumplimiento>;
  }

  async getDashboardEmpresa(empresaId: number) {
    const res = await api.get(`/matriz-legal/empresas/${empresaId}/dashboard`);
    return res.data as MatrizLegalDashboard;
  }

  async getEstadisticasEmpresa(empresaId: number) {
    const res = await api.get(`/matriz-legal/empresas/${empresaId}/estadisticas`);
    return res.data as MatrizLegalEstadisticas;
  }

  async updateCumplimiento(
    empresaId: number,
    normaId: number,
    data: Partial<MatrizLegalCumplimiento>
  ) {
    const res = await api.put(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/${normaId}`,
      data
    );
    return res.data as MatrizLegalCumplimiento;
  }

  async bulkUpdateCumplimiento(empresaId: number, payload: BulkUpdatePayload) {
    const res = await api.post(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/bulk`,
      payload
    );
    return res.data as { message: string };
  }

  async bulkUpdatePorFiltros(empresaId: number, payload: FiltrosBulkUpdatePayload) {
    const res = await api.post(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/bulk-filtros`,
      payload
    );
    return res.data as { message: string };
  }

  async inlineUpdateEstado(
    empresaId: number,
    normaId: number,
    estado: EstadoCumplimiento
  ) {
    const res = await api.patch(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/${normaId}/estado`,
      { estado }
    );
    return res.data as MatrizLegalCumplimiento;
  }

  async getHistorialCumplimiento(empresaId: number, cumplimientoId: number) {
    const res = await api.get(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/${cumplimientoId}/historial`
    );
    return res.data as MatrizLegalCumplimientoHistorial[];
  }

  // ==================== EXPORTACIÓN ====================

  async exportMatrizEmpresa(empresaId: number, incluirNoAplicables: boolean = false) {
    const res = await api.get(
      `/matriz-legal/empresas/${empresaId}/export/excel`,
      {
        params: { incluir_no_aplicables: incluirNoAplicables },
        responseType: "blob",
      }
    );
    return res.data as Blob;
  }

  /**
   * Importa el mismo Excel de exportMatrizEmpresa, ya diligenciado con
   * evidencia/observaciones/plan de acción/responsable/fecha compromiso/
   * seguimiento. Solo actualiza celdas con contenido — nunca borra con celdas
   * vacías.
   */
  async importarCumplimientoExcel(
    empresaId: number,
    file: File,
  ): Promise<ImportarCumplimientoResult> {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.post(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/importar-excel`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data as ImportarCumplimientoResult;
  }

  async exportTodasNormas(params?: {
    clasificacion?: string;
    sector_economico_id?: number;
  }) {
    const res = await api.get("/matriz-legal/normas/export/excel", {
      params,
      responseType: "blob",
    });
    return res.data as Blob;
  }

  // ==================== SUGERENCIAS IA ====================

  /**
   * Genera sugerencias de cumplimiento usando IA.
   */
  async getSugerenciasIA(normaId: number): Promise<SugerenciasIAResponse> {
    const res = await api.post(`/matriz-legal/normas/${normaId}/sugerencias-ia`);
    return res.data as SugerenciasIAResponse;
  }

  /**
   * Lanza la generación de sugerencias personalizadas POR NORMA en segundo
   * plano. Devuelve el id del job de inmediato; el progreso se consulta con
   * `getSugerenciasIAJob`.
   */
  async generarSugerenciasIABulk(
    empresaId: number,
    payload: SugerenciasIABulkPayload,
  ): Promise<SugerenciasIAJobCreated> {
    const res = await api.post(
      `/matriz-legal/empresas/${empresaId}/sugerencias-ia/bulk`,
      payload,
    );
    return res.data as SugerenciasIAJobCreated;
  }

  /**
   * Todos los cumplimiento_ids que coinciden con los filtros, sin tope de
   * tamaño — para trocearlos en tandas y lanzar varios jobs en secuencia.
   */
  async listarIdsSugerenciasIA(
    empresaId: number,
    filtros: Pick<
      SugerenciasIABulkPayload,
      "estado_cumplimiento" | "clasificacion" | "tema_general" | "q" | "solo_aplicables"
    >,
  ): Promise<number[]> {
    const res = await api.get(
      `/matriz-legal/empresas/${empresaId}/sugerencias-ia/ids`,
      { params: filtros },
    );
    return res.data as number[];
  }

  /** Progreso de un job de sugerencias de IA. */
  async getSugerenciasIAJob(
    empresaId: number,
    jobId: number,
  ): Promise<SugerenciasIAJobStatus> {
    const res = await api.get(
      `/matriz-legal/empresas/${empresaId}/sugerencias-ia/jobs/${jobId}`,
    );
    return res.data as SugerenciasIAJobStatus;
  }

  // ============ RESPONSABLES POR CLASIFICACIÓN (autoasignación) ============

  async getResponsablesClasificacion(
    empresaId: number,
  ): Promise<MatrizLegalResponsableClasificacion[]> {
    const res = await api.get(
      `/matriz-legal/empresas/${empresaId}/responsables-clasificacion`,
    );
    return res.data as MatrizLegalResponsableClasificacion[];
  }

  async upsertResponsableClasificacion(
    empresaId: number,
    clasificacionNorma: string,
    responsable: string,
  ): Promise<MatrizLegalResponsableClasificacion> {
    const res = await api.post(
      `/matriz-legal/empresas/${empresaId}/responsables-clasificacion`,
      { clasificacion_norma: clasificacionNorma, responsable },
    );
    return res.data as MatrizLegalResponsableClasificacion;
  }

  async deleteResponsableClasificacion(
    empresaId: number,
    mapeoId: number,
  ): Promise<void> {
    await api.delete(
      `/matriz-legal/empresas/${empresaId}/responsables-clasificacion/${mapeoId}`,
    );
  }

  async autoasignarResponsables(
    empresaId: number,
  ): Promise<AutoasignarResponsablesResult> {
    const res = await api.post(
      `/matriz-legal/empresas/${empresaId}/cumplimiento/autoasignar-responsables`,
    );
    return res.data as AutoasignarResponsablesResult;
  }

  // ==================== HELPERS ====================

  /**
   * Descarga un blob como archivo.
   */
  downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Obtiene el color para un estado de cumplimiento.
   */
  getColorEstadoCumplimiento(estado: EstadoCumplimiento | null): string {
    switch (estado) {
      case "cumple":
        return "#4caf50"; // Verde
      case "no_cumple":
        return "#f44336"; // Rojo
      case "pendiente":
        return "#ff9800"; // Naranja
      case "en_proceso":
        return "#2196f3"; // Azul
      case "no_aplica":
        return "#9e9e9e"; // Gris
      default:
        return "#ff9800"; // Naranja por defecto (pendiente)
    }
  }

  /**
   * Obtiene la etiqueta para un estado de cumplimiento.
   */
  getLabelEstadoCumplimiento(estado: EstadoCumplimiento | null): string {
    switch (estado) {
      case "cumple":
        return "Cumple";
      case "no_cumple":
        return "No Cumple";
      case "pendiente":
        return "Pendiente";
      case "en_proceso":
        return "En Proceso";
      case "no_aplica":
        return "No Aplica";
      default:
        return "Pendiente";
    }
  }
}

export const matrizLegalService = new MatrizLegalService();
export default matrizLegalService;
